/**
 * The sidebar region: the Assist wordmark and the theme toggle, New chat,
 * Search, Gallery, Artifacts, Dispatch (a dot when a PC is online), the projects, the
 * recent chats, and the person's chip at the foot with their 5-hour bar. Rows are links, so they open in a new tab like any
 * other link and read as navigation to assistive tech.
 *
 * A recent chat's context menu (right-click, the menu key or Shift+F10)
 * renames it in place or deletes it.
 */

import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent
} from 'react';
import { createProject, deleteChat, patchChat, type ChatSummary } from '../api';
import { inDesktop } from '../desktop';
import { ds } from '../ds';
import { stopTurn } from '../hooks/useTurns';
import { Link, matchRoute, useRouter } from '../router';
import { useApp } from '../state';
import type { Theme } from '../theme';
import { UsageBar } from './composer/Usage';
import { PcDot } from './DevicePicker';
import { Menu, MenuDivider, MenuItem, MonoLabel, PROJECT_COLOURS, Ph, useDismiss } from './ui';

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

/** A row, 44px tall under a finger. */
const rowStyle = (active: boolean, touch: boolean): CSSProperties => ({
  minHeight: touch ? 44 : 34,
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  padding: '0 10px',
  borderRadius: 'var(--radius-sm)',
  fontSize: 13,
  background: active ? 'var(--primary-soft)' : 'transparent',
  color: active ? 'var(--primary-text)' : 'var(--text-body)',
  flex: 'none'
});

/** A recent chat and the row it was opened from, to give focus back to. */
interface ChatTarget {
  chat: ChatSummary;
  row: HTMLElement;
}

/** The recent list's context menu: at the pointer, kept inside the window. */
function ChatMenu({
  x,
  y,
  onRename,
  onDelete,
  onClose
}: {
  x: number;
  y: number;
  onRename: () => void;
  onDelete: () => void;
  /** `refocus` when closed from the keyboard, so focus goes back to the row. */
  onClose: (refocus: boolean) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y });
  useDismiss(ref, true, () => onClose(false));

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    setPos({
      left: Math.max(8, Math.min(x, window.innerWidth - width - 8)),
      top: Math.max(8, Math.min(y, window.innerHeight - height - 8))
    });
    el.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [x, y]);

  const onKeyDown = (e: KeyboardEvent) => {
    const items = Array.from(ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const at = items.indexOf(document.activeElement as HTMLElement);
      const step = e.key === 'ArrowDown' ? 1 : -1;
      items[(at + step + items.length) % items.length]?.focus();
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      // Stopped here so the shell's Escape does not close the drawer too.
      e.preventDefault();
      e.stopPropagation();
      onClose(true);
    }
  };

  return (
    // The menu key's own `contextmenu` can land on the menu once it is open.
    <div style={{ display: 'contents' }} onKeyDown={onKeyDown} onContextMenu={(e) => e.preventDefault()}>
      <Menu ref={ref} style={{ position: 'fixed', left: pos.left, top: pos.top, width: 180, zIndex: 40 }}>
        <MenuItem icon="pencil-simple" label="Rename" onSelect={onRename} />
        <MenuDivider />
        <MenuItem
          icon="trash"
          label="Delete"
          iconColor="var(--danger-500)"
          onSelect={onDelete}
          style={{ color: 'var(--danger-600)' }}
        />
      </Menu>
    </div>
  );
}

/** A recent chat's row while it is renamed. Enter or leaving the field saves;
 *  Escape keeps the name it had. */
function RenameField({
  chat,
  active,
  touch,
  onDone
}: {
  chat: ChatSummary;
  active: boolean;
  touch: boolean;
  /** `refocus` when finished from the keyboard. */
  onDone: (refocus: boolean) => void;
}) {
  const { upsertChat } = useApp();
  const [value, setValue] = useState(chat.title);
  const ref = useRef<HTMLInputElement>(null);
  const finished = useRef(false);

  useLayoutEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);

  const finish = (save: boolean, refocus: boolean) => {
    // Enter unmounts the field, and a blur can follow it.
    if (finished.current) return;
    finished.current = true;
    onDone(refocus);
    const title = value.trim();
    if (!save || !title || title === chat.title) return;
    upsertChat({ ...chat, title });
    patchChat(chat.id, { title })
      .then(upsertChat)
      .catch(() => upsertChat(chat));
  };

  return (
    <input
      ref={ref}
      value={value}
      aria-label="Chat name"
      placeholder="New chat"
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => finish(true, false)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          finish(true, true);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          finish(false, true);
        }
      }}
      style={{
        ...rowStyle(active, touch),
        width: '100%',
        boxSizing: 'border-box',
        border: 0,
        background: 'var(--surface-card)',
        color: 'var(--text-strong)',
        // Inset, because the list scrolls and would clip an outer ring.
        boxShadow: 'inset 0 0 0 1.5px var(--primary)'
      }}
    />
  );
}

export function Sidebar({
  drawer,
  touch,
  theme,
  onToggleTheme,
  onSearch,
  onNavigate
}: {
  drawer: boolean;
  /** Taller rows, and no keyboard hint. */
  touch: boolean;
  theme: Theme;
  onToggleTheme: () => void;
  onSearch: () => void;
  /** Any row was chosen — the drawer closes, even when the route does not
   *  change (New chat from Home). */
  onNavigate: () => void;
}) {
  const { me, chats, projects, devices, refreshLists, removeChat } = useApp();
  const { path, navigate } = useRouter();
  const [creating, setCreating] = useState(false);
  const [menuAt, setMenuAt] = useState<(ChatTarget & { x: number; y: number }) | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<ChatTarget | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const recentRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const { Wordmark, Button, Dialog } = ds();

  const activeChat = matchRoute('/chat/:id', path)?.id ?? null;
  const activeProject = matchRoute('/project/:id', path)?.id ?? null;
  const dispatchOn = path === '/dispatch' || path.startsWith('/dispatch/');
  const caps = me?.capabilities;
  const desktop = inDesktop();

  const themeToggle = (
    <button
      type="button"
      className="bare hov-soft tap"
      aria-label={theme === 'dark' ? 'Use the light theme' : 'Use the dark theme'}
      title="Theme"
      onClick={onToggleTheme}
      style={{
        width: 30,
        height: 28,
        flex: 'none',
        display: 'grid',
        placeItems: 'center',
        borderRadius: 'var(--radius-chrome)',
        color: 'var(--text-muted)'
      }}
    >
      <Ph name="circle-half" size={16} />
    </button>
  );

  /** Without a point, the menu opens under the row: the keyboard's way in. */
  const openMenu = (chat: ChatSummary, row: HTMLElement, point?: { x: number; y: number }) => {
    const r = row.getBoundingClientRect();
    setMenuAt({ chat, row, ...(point ?? { x: r.left + 16, y: r.bottom + 4 }) });
  };

  const onRowContextMenu = (chat: ChatSummary, e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const r = e.currentTarget.getBoundingClientRect();
    // A keyboard-opened `contextmenu` can carry a point that is nowhere near
    // the row (0, 0 in some browsers); only a point on the row is used.
    const onRow = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
    openMenu(chat, e.currentTarget, onRow ? { x: e.clientX, y: e.clientY } : undefined);
  };

  const onRowKeyDown = (chat: ChatSummary, e: KeyboardEvent<HTMLAnchorElement>) => {
    if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
      e.preventDefault();
      openMenu(chat, e.currentTarget);
    }
  };

  const focusRow = (id: string) => {
    requestAnimationFrame(() => {
      const rows = recentRef.current?.querySelectorAll<HTMLElement>('a[href]') ?? [];
      Array.from(rows)
        .find((a) => a.getAttribute('href') === `/chat/${id}`)
        ?.focus();
    });
  };

  const cancelDelete = () => {
    if (deleting) return;
    if (confirming?.row.isConnected) confirming.row.focus();
    setConfirming(null);
  };
  // The design system's Dialog has no Escape of its own. Its scrim covers
  // the window, so a pointer never lands outside it.
  useDismiss(dialogRef, !!confirming, cancelDelete);

  const removeConfirmed = async () => {
    if (!confirming) return;
    const { id } = confirming.chat;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteChat(id);
      // A reply still streaming into it would put it back in the list.
      stopTurn(id);
      removeChat(id);
      setConfirming(null);
      if (id === activeChat) navigate('/');
    } catch {
      setDeleteError('That did not go through. Try again.');
    } finally {
      setDeleting(false);
    }
  };

  const newProject = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const project = await createProject({});
      await refreshLists();
      navigate(`/project/${project.id}`);
    } catch {
      // Nothing was made. The + is still there to try again.
    } finally {
      setCreating(false);
    }
  };

  return (
    <nav
      aria-label="Assist"
      className={drawer ? 'drawer-in' : undefined}
      style={{
        width: 264,
        maxWidth: drawer ? 'calc(100% - 48px)' : undefined,
        flex: 'none',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: '14px 10px 10px',
        background: 'var(--app-chrome-panel)',
        // A drawer meets the screen's edge square, and rounds where it ends.
        borderRadius: drawer ? '0 var(--radius-region) var(--radius-region) 0' : 'var(--radius-region)',
        position: drawer ? 'absolute' : 'relative',
        top: 0,
        bottom: 0,
        left: 0,
        zIndex: 30,
        boxShadow: drawer ? 'var(--shadow-xl)' : undefined
      }}
    >
      {/* The desktop app's title bar carries the wordmark: the sidebar does not
          say it twice, and the theme toggle moves up beside New chat. */}
      {desktop ? null : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 6px 12px' }}>
          <Wordmark lockup="ink" word="assist" badge="sparkle" size={17} />
          {themeToggle}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Button
            variant="accent"
            density="app"
            size="sm"
            iconLeft="note-pencil"
            fullWidth
            onClick={() => {
              onNavigate();
              navigate('/');
            }}
          >
            New chat
          </Button>
        </div>
        {desktop ? themeToggle : null}
      </div>

      <button
        type="button"
        className="bare hov-soft"
        onClick={onSearch}
        style={{
          height: touch ? 44 : 34,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '0 10px',
          marginTop: 6,
          borderRadius: 'var(--radius-sm)',
          color: 'var(--text-muted)',
          fontSize: 13
        }}
      >
        <Ph name="magnifying-glass" size={15} />
        <span style={{ flex: 1 }}>Search</span>
        {touch ? null : (
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-faint)' }}>
            {IS_MAC ? 'Cmd K' : 'Ctrl K'}
          </span>
        )}
      </button>

      {caps?.image ? (
        <Link
          to="/gallery"
          onClick={onNavigate}
          className={path === '/gallery' ? undefined : 'hov-soft'}
          style={{
            ...rowStyle(path === '/gallery', touch),
            gap: 8,
            color: path === '/gallery' ? 'var(--primary-text)' : 'var(--text-muted)'
          }}
        >
          <Ph name="images" size={15} />
          <span style={{ flex: 1 }}>Gallery</span>
        </Link>
      ) : null}

      <Link
        to="/artifacts"
        onClick={onNavigate}
        className={path === '/artifacts' ? undefined : 'hov-soft'}
        style={{
          ...rowStyle(path === '/artifacts', touch),
          gap: 8,
          color: path === '/artifacts' ? 'var(--primary-text)' : 'var(--text-muted)'
        }}
      >
        <Ph name="shapes" size={15} />
        <span style={{ flex: 1 }}>Artifacts</span>
      </Link>

      <Link
        to="/dispatch"
        onClick={onNavigate}
        className={dispatchOn ? undefined : 'hov-soft'}
        style={{
          ...rowStyle(dispatchOn, touch),
          gap: 8,
          color: dispatchOn ? 'var(--primary-text)' : 'var(--text-muted)'
        }}
      >
        <Ph name="desktop" size={15} />
        <span style={{ flex: 1 }}>Dispatch</span>
        {devices?.some((d) => d.online) ? <PcDot online /> : null}
      </Link>

      <div style={{ display: 'flex', alignItems: 'center', padding: '16px 4px 6px 10px' }}>
        <MonoLabel style={{ flex: 1 }}>Projects</MonoLabel>
        <button
          type="button"
          className="bare hov-soft tap"
          aria-label="New project"
          title="New project"
          onClick={() => void newProject()}
          style={{
            width: 22,
            height: 22,
            display: 'grid',
            placeItems: 'center',
            borderRadius: 'var(--radius-chrome)',
            color: 'var(--text-faint)'
          }}
        >
          <Ph name={creating ? 'circle-notch' : 'plus'} size={13} className={creating ? 'spin' : undefined} />
        </button>
      </div>
      {projects.map((p) => {
        const on = p.id === activeProject;
        return (
          <Link key={p.id} to={`/project/${p.id}`} onClick={onNavigate} className="hov-tint" style={rowStyle(on, touch)}>
            <span
              style={{
                width: 8,
                height: 8,
                flex: 'none',
                borderRadius: 3,
                background: PROJECT_COLOURS[p.colour]?.dot ?? 'var(--primary)'
              }}
            />
            <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {p.name || 'Untitled project'}
            </span>
          </Link>
        );
      })}

      <MonoLabel style={{ padding: '16px 10px 6px' }}>Recent</MonoLabel>
      <div
        ref={recentRef}
        className="scroll"
        style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        {chats.map((c) => {
          const on = c.id === activeChat;
          if (c.id === renaming) {
            return (
              <RenameField
                key={c.id}
                chat={c}
                active={on}
                touch={touch}
                onDone={(refocus) => {
                  setRenaming(null);
                  if (refocus) focusRow(c.id);
                }}
              />
            );
          }
          return (
            <Link
              key={c.id}
              to={`/chat/${c.id}`}
              onClick={onNavigate}
              onContextMenu={(e) => onRowContextMenu(c, e)}
              onKeyDown={(e) => onRowKeyDown(c, e)}
              className="hov-tint"
              style={{ ...rowStyle(on, touch), gap: 8 }}
            >
              {c.automationId ? <Ph name="clock-countdown" size={14} style={{ flex: 'none', color: 'var(--text-muted)' }} /> : null}
              <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {c.title || 'New chat'}
              </span>
              {c.needsYou && !on ? (
                <span
                  aria-label="Waiting for you"
                  style={{ width: 7, height: 7, flex: 'none', borderRadius: 999, background: 'var(--warning-500)' }}
                />
              ) : null}
            </Link>
          );
        })}
      </div>

      {menuAt ? (
        <ChatMenu
          key={`${menuAt.chat.id}:${menuAt.x}:${menuAt.y}`}
          x={menuAt.x}
          y={menuAt.y}
          onClose={(refocus) => {
            if (refocus) menuAt.row.focus();
            setMenuAt(null);
          }}
          onRename={() => {
            setMenuAt(null);
            setRenaming(menuAt.chat.id);
          }}
          onDelete={() => {
            setMenuAt(null);
            setDeleteError(null);
            setConfirming({ chat: menuAt.chat, row: menuAt.row });
          }}
        />
      ) : null}

      {confirming ? (
        <div ref={dialogRef} style={{ display: 'contents' }}>
          <Dialog
            open
            position="fixed"
            width={420}
            title="Delete this chat?"
            onClose={cancelDelete}
            style={{ borderRadius: 'var(--radius-dialog)', padding: 24, gap: 16 }}
            footer={
              <>
                <Button variant="ghost" density="app" size="sm" autoFocus disabled={deleting} onClick={cancelDelete}>
                  Cancel
                </Button>
                <Button variant="danger" density="app" size="sm" disabled={deleting} onClick={() => void removeConfirmed()}>
                  Delete
                </Button>
              </>
            }
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <p style={{ margin: 0, fontSize: 14, color: 'var(--text-body)' }}>Its messages and files are deleted too.</p>
              {deleteError ? <span style={{ fontSize: 12.5, color: 'var(--danger-600)' }}>{deleteError}</span> : null}
            </div>
          </Dialog>
        </div>
      ) : null}

      {me ? (
        <Link
          to="/settings"
          onClick={onNavigate}
          className="hov-lift"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: 10,
            marginTop: 6,
            borderRadius: 'var(--radius-app-card)',
            background: 'var(--app-panel)',
            flex: 'none'
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 32,
              height: 32,
              flex: 'none',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--mauve-800)',
              color: 'var(--mauve-200)',
              display: 'grid',
              placeItems: 'center',
              fontWeight: 600,
              fontSize: 12,
              overflow: 'hidden'
            }}
          >
            {me.user.avatarUrl ? (
              <img
                src={me.user.avatarUrl}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            ) : (
              me.user.initials
            )}
          </span>
          <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--text-strong)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {me.user.name}
            </span>
            {me.usage.month ? (
              <UsageBar pct={me.usage.month.pct ?? 0} used={me.usage.month.spentPence} />
            ) : me.usage.fiveHour.allowance !== null ? (
              <UsageBar pct={me.usage.fiveHour.pct} used={me.usage.fiveHour.used} />
            ) : null}
          </span>
          <Ph name="gear-six" size={16} color="var(--text-muted)" />
          <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
            Settings
          </span>
        </Link>
      ) : null}
    </nav>
  );
}
